<template>
  <div class="notif-shell" :class="{ 'notif-shell--embedded': embedded }">
    <!-- Header -->
    <div class="notif-header">
      <button v-if="!embedded" class="notif-back" type="button" @click="router.back()">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        Voltar
      </button>
      <h1 class="notif-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        Notificações
      </h1>
      <label v-if="items.length > 0" class="notif-select-all" title="Selecionar todas as carregadas">
        <input
          ref="selectAllEl"
          type="checkbox"
          class="notif-cb"
          :checked="isAllSelected"
          @change="toggleSelectAll"
        />
        Selecionar todos
      </label>
      <div class="notif-bulk" v-if="selectedIds.size > 0">
        <span class="notif-bulk-count">{{ selectedIds.size }} selecionada{{ selectedIds.size !== 1 ? 's' : '' }}</span>
        <button class="notif-bulk-btn notif-bulk-read" type="button" :disabled="acting" @click="markSelectedRead">
          Marcar como lida
        </button>
        <button class="notif-bulk-btn notif-bulk-del" type="button" :disabled="acting" @click="deleteSelected">
          Excluir
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="notif-filters">
      <input
        v-model="search"
        type="text"
        class="notif-input"
        placeholder="Buscar por título ou mensagem..."
        @input="onFilterChange"
      />
      <select v-model="statusFilter" class="notif-select" @change="onFilterChange">
        <option value="all">Todas</option>
        <option value="unread">Não lidas</option>
        <option value="read">Lidas</option>
      </select>
      <select v-model="typeFilter" class="notif-select" @change="onFilterChange">
        <option value="all">Todos os tipos</option>
        <option v-for="t in types" :key="t" :value="t">{{ typeLabel(t) }}</option>
      </select>
      <input v-model="dateFrom" type="date" class="notif-input notif-input--date" @change="onFilterChange" />
      <span class="notif-date-sep">até</span>
      <input v-model="dateTo" type="date" class="notif-input notif-input--date" @change="onFilterChange" />
      <button v-if="hasActiveFilters" class="notif-clear-filters" type="button" @click="clearFilters">
        Limpar filtros
      </button>
    </div>

    <!-- List -->
    <div class="notif-list" ref="listEl" @scroll="onScroll">
      <div v-if="loading && items.length === 0" class="notif-skeleton-wrap">
        <SkeletonRow v-for="i in 6" :key="i" :columns="[70, 90, 30]" />
      </div>

      <div v-else-if="items.length === 0" class="notif-empty">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        <p>Nenhuma notificação encontrada{{ hasActiveFilters ? ' com esses filtros' : '' }}.</p>
      </div>

      <template v-else>
        <div
          v-for="item in items"
          :key="item.id"
          class="notif-item"
          :class="{ 'notif-item--sel': selectedIds.has(item.id) }"
        >
          <label class="notif-cb-label" @click.stop>
            <input
              type="checkbox"
              class="notif-cb"
              :checked="selectedIds.has(item.id)"
              @change="toggleSelect(item.id)"
            />
          </label>
          <button class="notif-item-body" type="button" @click="openItem(item)">
            <div class="notif-item-title">
              {{ item.title }}
              <span v-if="!item.readAt" class="notif-pill">novo</span>
            </div>
            <div class="notif-item-text">{{ item.message }}</div>
            <div class="notif-item-meta">
              <span class="notif-item-type">{{ typeLabel(item.type) }}</span>
              <span v-if="item.createdAt" class="notif-item-when">{{ formatDateTime(item.createdAt) }}</span>
            </div>
          </button>
        </div>

        <div v-if="loadingMore" class="notif-loading-more">Carregando mais...</div>
        <div v-else-if="!hasMore" class="notif-end">Fim das notificações.</div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import SkeletonRow from '@/components/skeleton/SkeletonRow.vue'
import { apiFetch } from '@/store/api'
import { notificationsStore } from '@/store/notifications'
import type { AppNotification } from '@/store/notifications'
import { formatDateTime } from '@/utils/date'

const PAGE_SIZE = 25

const TYPE_LABELS: Record<string, string> = {
  'task-start': 'Tarefa iniciando',
  'task-end': 'Tarefa finalizando',
  'task-end-tomorrow': 'Tarefa finaliza amanhã',
  'task-overdue': 'Tarefa em atraso',
  'task-assigned': 'Tarefa atribuída',
  'task-comment': 'Comentário em tarefa',
  'task-deadline': 'Prazo se aproximando',
  'dds-confirmation': 'Confirmação de DDS',
  maintenance: 'Manutenção',
  'warehouse-tool-request': 'Requisição de ferramentas',
  'warehouse-low-stock': 'Estoque baixo',
  'warehouse-pickup-confirm': 'Confirmação de retirada',
  'warehouse-request-return': 'Devolução solicitada',
  'warehouse-request-provided-check': 'Confirmar fornecimento',
  'warehouse-tool-approved': 'Ferramentas aprovadas',
  'warehouse-tool-rejected': 'Ferramentas reprovadas',
  'warehouse-damage-resolved': 'Avaria resolvida',
  'ticket-assigned-role': 'Novo chamado',
  'ticket-project-observer': 'Chamado no projeto',
  'ticket-status': 'Atualização de chamado',
  'ticket-comment': 'Mensagem em chamado'
}

interface SearchResponse {
  items: AppNotification[]
  total: number
  limit: number
  offset: number
  types: string[]
}

export default defineComponent({
  name: 'NotificationsView',
  components: { SkeletonRow },
  props: {
    embedded: { type: Boolean, default: false }
  },
  setup() {
    const router = useRouter()

    const items = ref<AppNotification[]>([])
    const total = ref(0)
    const types = ref<string[]>([])
    const loading = ref(false)
    const loadingMore = ref(false)
    const offset = ref(0)
    const listEl = ref<HTMLElement | null>(null)

    const search = ref('')
    const statusFilter = ref<'all' | 'unread' | 'read'>('all')
    const typeFilter = ref('all')
    const dateFrom = ref('')
    const dateTo = ref('')

    const selectedIds = ref(new Set<string>())
    const acting = ref(false)
    const selectAllEl = ref<HTMLInputElement | null>(null)

    let filterDebounce: ReturnType<typeof setTimeout> | null = null

    const isAllSelected = computed(
      () => items.value.length > 0 && selectedIds.value.size === items.value.length
    )
    const isSomeSelected = computed(() => selectedIds.value.size > 0 && !isAllSelected.value)

    watchEffect(() => {
      if (selectAllEl.value) {
        selectAllEl.value.indeterminate = isSomeSelected.value
      }
    })

    function toggleSelectAll() {
      if (isAllSelected.value) {
        selectedIds.value = new Set()
      } else {
        selectedIds.value = new Set(items.value.map((n) => n.id))
      }
    }

    const hasMore = computed(() => items.value.length < total.value)
    const hasActiveFilters = computed(
      () =>
        search.value.trim() !== '' ||
        statusFilter.value !== 'all' ||
        typeFilter.value !== 'all' ||
        dateFrom.value !== '' ||
        dateTo.value !== ''
    )

    const typeLabel = (type: string) => TYPE_LABELS[type] || type

    function buildQuery(nextOffset: number) {
      const params = new URLSearchParams()
      params.set('limit', String(PAGE_SIZE))
      params.set('offset', String(nextOffset))
      if (statusFilter.value !== 'all') params.set('status', statusFilter.value)
      if (typeFilter.value !== 'all') params.set('type', typeFilter.value)
      if (search.value.trim()) params.set('search', search.value.trim())
      if (dateFrom.value) params.set('dateFrom', dateFrom.value)
      if (dateTo.value) params.set('dateTo', dateTo.value)
      return params.toString()
    }

    async function loadFirstPage() {
      loading.value = true
      offset.value = 0
      try {
        const res = await apiFetch<SearchResponse>(`/notifications/search?${buildQuery(0)}`)
        items.value = res.items
        total.value = res.total
        types.value = res.types
        offset.value = res.items.length
      } finally {
        loading.value = false
      }
    }

    async function loadMore() {
      if (loadingMore.value || loading.value || !hasMore.value) return
      loadingMore.value = true
      try {
        const res = await apiFetch<SearchResponse>(`/notifications/search?${buildQuery(offset.value)}`)
        items.value = items.value.concat(res.items)
        total.value = res.total
        offset.value += res.items.length
      } finally {
        loadingMore.value = false
      }
    }

    function onScroll() {
      const el = listEl.value
      if (!el) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        loadMore()
      }
    }

    function onFilterChange() {
      if (filterDebounce) clearTimeout(filterDebounce)
      filterDebounce = setTimeout(() => {
        selectedIds.value = new Set()
        loadFirstPage()
      }, 250)
    }

    function clearFilters() {
      search.value = ''
      statusFilter.value = 'all'
      typeFilter.value = 'all'
      dateFrom.value = ''
      dateTo.value = ''
      selectedIds.value = new Set()
      loadFirstPage()
    }

    function toggleSelect(id: string) {
      const next = new Set(selectedIds.value)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      selectedIds.value = next
    }

    async function markSelectedRead() {
      acting.value = true
      try {
        const ids = Array.from(selectedIds.value)
        await notificationsStore.markManyRead(ids)
        items.value = items.value.map((it) => (ids.includes(it.id) ? { ...it, readAt: new Date().toISOString() } : it))
        selectedIds.value = new Set()
      } finally {
        acting.value = false
      }
    }

    async function deleteSelected() {
      acting.value = true
      try {
        const ids = Array.from(selectedIds.value)
        await notificationsStore.deleteMany(ids)
        items.value = items.value.filter((it) => !ids.includes(it.id))
        total.value = Math.max(0, total.value - ids.length)
        offset.value = items.value.length
        selectedIds.value = new Set()
      } finally {
        acting.value = false
      }
    }

    async function openItem(item: AppNotification) {
      if (!item.readAt) {
        item.readAt = new Date().toISOString()
        await notificationsStore.markRead(item.id).catch(() => null)
      }
      if (item.relatedType && item.relatedId) {
        router.push({ path: '/home', query: { notif: item.id } })
      }
    }

    onMounted(() => {
      loadFirstPage()
    })

    return {
      router,
      items,
      types,
      loading,
      loadingMore,
      hasMore,
      listEl,
      search,
      statusFilter,
      typeFilter,
      dateFrom,
      dateTo,
      selectedIds,
      acting,
      selectAllEl,
      isAllSelected,
      hasActiveFilters,
      typeLabel,
      onScroll,
      onFilterChange,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      markSelectedRead,
      deleteSelected,
      openItem,
      formatDateTime
    }
  }
})
</script>

<style scoped lang="scss">
.notif-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);

  &.notif-shell--embedded {
    min-height: unset;
    height: 100%;
    background: transparent;
  }
}

.notif-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 28px 14px;
  border-bottom: 1px solid oklch(0.5 0 0 / 0.1);
  flex-wrap: wrap;
}

.notif-back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink-muted);
  padding: 0;
  transition: color 0.15s;
  &:hover { color: var(--ink); }
}

.notif-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  flex: 1;
  color: var(--ink);
}

.notif-select-all {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--ink-muted);
  cursor: pointer;
  user-select: none;
}

.notif-bulk {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notif-bulk-count {
  font-size: 12.5px;
  color: var(--ink-muted);
}

.notif-bulk-btn {
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.notif-bulk-read {
  background: oklch(0.55 0.15 240 / 0.1);
  color: var(--accent);
  border-color: oklch(0.55 0.15 240 / 0.25);
  &:hover:not(:disabled) { background: oklch(0.55 0.15 240 / 0.18); }
}

.notif-bulk-del {
  background: oklch(0.55 0.2 25 / 0.1);
  color: #c0392b;
  border-color: oklch(0.55 0.2 25 / 0.25);
  &:hover:not(:disabled) { background: oklch(0.55 0.2 25 / 0.18); }
}

.notif-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 14px 28px;
  border-bottom: 1px solid oklch(0.5 0 0 / 0.08);
}

.notif-input,
.notif-select {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid oklch(0.5 0 0 / 0.16);
  font-size: 13px;
  background: var(--card);
  color: var(--ink);
}

.notif-input {
  flex: 1;
  min-width: 180px;
}

.notif-input--date {
  flex: none;
  width: 150px;
}

.notif-date-sep {
  font-size: 12px;
  color: var(--ink-muted);
}

.notif-clear-filters {
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--accent);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  &:hover { text-decoration: underline; }
}

.notif-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 28px 28px;
}

.notif-skeleton-wrap {
  padding-top: 20px;
}

.notif-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 72px 28px;
  color: var(--ink-muted);
  text-align: center;
  svg { opacity: 0.3; }
  p { margin: 0; font-size: 14px; }
}

.notif-item {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid oklch(0.5 0 0 / 0.08);
  transition: background 0.1s;
  &:first-child { border-top: 1px solid oklch(0.5 0 0 / 0.08); margin-top: 16px; }
  &--sel { background: oklch(0.5 0 0 / 0.06); }
  &:not(.notif-item--sel):hover { background: oklch(0.5 0 0 / 0.03); }
}

.notif-cb-label {
  display: flex;
  align-items: center;
  padding: 14px 10px 14px 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.notif-cb {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: var(--accent);
}

.notif-item-body {
  flex: 1;
  border: none;
  background: transparent;
  text-align: left;
  padding: 14px 4px 14px 0;
  cursor: pointer;
  min-width: 0;
}

.notif-item-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ink);
}

.notif-item-text {
  margin-top: 4px;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--ink-muted);
}

.notif-item-meta {
  margin-top: 6px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.notif-item-type {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: oklch(0.5 0 0 / 0.08);
  color: var(--ink-muted);
}

.notif-item-when {
  font-size: 11px;
  color: var(--ink-muted);
  font-variant-numeric: tabular-nums;
}

.notif-pill {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #d4452f;
  color: #fff;
  font-weight: 600;
}

.notif-loading-more,
.notif-end {
  text-align: center;
  padding: 16px;
  font-size: 12.5px;
  color: var(--ink-muted);
}
</style>
